import React, { useCallback, useRef } from 'react'
import { getGroupResizeAlignment } from '../utils/canvasGroupResize'
import { getGroupAlignment } from '../utils/canvasAlignment'
import { DUPLICATE_DRAG_THRESHOLD } from '../utils/canvasDuplicate'
import type {
  AlignmentLines,
  Bounds,
  MovingRect,
  ResizeCorner,
  SizeLimits,
} from '../utils/canvasAlignment'

/** Die Auswahl, wie sie durch den Hook läuft: Kennung plus Rechteck. */
type Entry = { id: string; rect: MovingRect }

interface UseGroupDragOptions {
  /** Die ausgewählten Notizen, in der Lage vor dem Ziehen. */
  getRects: () => Entry[]
  /** Bildschirm- in Canvas-Koordinaten (React Flows `screenToFlowPosition`). */
  toFlow: (position: { x: number; y: number }) => { x: number; y: number }
  /**
   * Die Ausrichtungspartner — alles, was NICHT mitbewegt wird. Als Funktion,
   * weil der Aufrufer sie beim Anfassen einsammelt (siehe `onGestureStart`) und
   * je Bewegung auf den sichtbaren Ausschnitt filtert.
   */
  getAlignTargets: () => Bounds[]
  /**
   * Fangschwelle in Canvas-Pixeln. Als GETTER: Ein Zoomwechsel rendert
   * `CanvasFlow` bewusst nicht neu (siehe den `getZoom()`-Kommentar in
   * `handleNodesChange`) — ein durchgereichter Wert wäre nach dem ersten Pinch
   * veraltet.
   */
  threshold: () => number
  /**
   * Einmal beim Anfassen, mit der eingefrorenen Ausgangslage. Der Aufrufer
   * sammelt daran seine Ausrichtungspartner ein — er muss dafür wissen, WAS
   * sich gleich bewegt.
   */
  onGestureStart?: (start: Entry[]) => void
  /**
   * Live, je Zeigerbewegung. Die Linien kommen als zweites Argument heraus:
   * `setGuides` gehört `CanvasFlow`, nicht dem Hook.
   */
  onChange: (next: Entry[], lines: AlignmentLines) => void
  /** Beim Loslassen — hier wird festgeschrieben. */
  onCommit: (next: Entry[], lines: AlignmentLines) => void
  limits: SizeLimits
  grid: number
  /**
   * Alt + **Verschieben**, sobald der Zeiger `DUPLICATE_DRAG_THRESHOLD` hinter
   * sich hat. Bekommt die eingefrorene Ausgangslage — genau dort gehören die
   * Kopien hin, denn bis zur Schwelle sind die Elemente schon weiter.
   *
   * Nie beim **Skalieren**: ein Canvas-Element größer zu ziehen ist kein
   * Duplizieren.
   */
  onDuplicate?: (start: Entry[]) => void
}

/** Was eine Geste je Bewegung liefert: die neuen Rechtecke und ihre Linien. */
type GestureResult = AlignmentLines & { rects: MovingRect[] }

/**
 * Die beiden Zeiger-Gesten am Rahmen der Mehrfachauswahl: an einer Ecke
 * **skalieren**, an der Fläche dazwischen **verschieben**.
 *
 * Beide richten die Auswahl als GANZES aus — Bezug ist ihr umschließendes
 * Rechteck, nie das einzelne Element (siehe `getGroupAlignment`).
 *
 * **Eigenes Zeiger-Tracking**, weil React Flow für beides nichts anbietet, was
 * hier trüge: `NodeResizeControl` ist an genau einen Node gebunden, und das
 * `NodesSelection`-Rechteck (das ein `useDrag` mitbringt) erscheint nach
 * ⇧-Klick gar nicht.
 *
 * `setPointerCapture` hält die Geste auch dann, wenn der Zeiger den Griff
 * verlässt — bei 12px großen Punkten ist das die Regel, nicht die Ausnahme.
 *
 * @example
 * const { startResize, startMove } = useGroupDrag({ … })
 * <SelectionOverlay onResizeStart={startResize} onMoveStart={startMove} />
 */
export function useGroupDrag({
  getRects,
  toFlow,
  getAlignTargets,
  threshold,
  onGestureStart,
  onChange,
  onCommit,
  limits,
  grid,
  onDuplicate,
}: UseGroupDragOptions) {
  // Über Refs, damit die Handler stabil bleiben: Sie hängen an den Notizen,
  // und die ändern sich während des Ziehens jeden Frame.
  const optionsRef = useRef({
    getRects, toFlow, getAlignTargets, threshold,
    onGestureStart, onChange, onCommit, limits, grid, onDuplicate,
  })
  optionsRef.current = {
    getRects, toFlow, getAlignTargets, threshold,
    onGestureStart, onChange, onCommit, limits, grid, onDuplicate,
  }

  /**
   * Der gemeinsame Ablauf beider Gesten: Ausgangslage merken, bei jeder
   * Bewegung neu rechnen, beim Loslassen festschreiben.
   *
   * @param compute rechnet aus der Ausgangslage und der aktuellen Zeigerlage
   *                in Canvas-Koordinaten die neuen Rechtecke samt Linien
   * @param onPastThreshold einmalig, sobald der Zeiger
   *                `DUPLICATE_DRAG_THRESHOLD` hinter sich hat. Bewusst NICHT
   *                hier im `pointerdown` aufgerufen: sonst duplizierte schon
   *                ein Klick in die Lücke des Auswahlrahmens.
   */
  const begin = useCallback((
    event: React.PointerEvent,
    compute: (
      start: Entry[],
      pointer: { x: number; y: number },
      origin: { x: number; y: number },
    ) => GestureResult,
    onPastThreshold?: (start: Entry[]) => void,
  ) => {
    // Nicht an React Flow weiterreichen: Sonst startete die Pane einen
    // Auswahlrahmen oder ein Node seinen eigenen Ziehvorgang.
    event.preventDefault()
    event.stopPropagation()

    const { getRects } = optionsRef.current
    const start = getRects()
    if (start.length === 0) return

    // Erst jetzt melden, nicht vor dem Wächter: Ohne Auswahl gibt es keine
    // Geste, und der Aufrufer sammelte sonst Partner für nichts ein.
    optionsRef.current.onGestureStart?.(start)

    const target = event.currentTarget as HTMLElement
    target.setPointerCapture(event.pointerId)
    const origin = optionsRef.current.toFlow({ x: event.clientX, y: event.clientY })
    // Die Schwelle wird in CLIENT-Koordinaten gemessen, nicht in
    // Canvas-Koordinaten: So sind es echte Bildschirm-Pixel, ohne dass der
    // Hook den Zoom kennen müsste.
    const originClient = { x: event.clientX, y: event.clientY }
    let pastThreshold = false
    // Gerechnet wird IMMER aus der Anfangslage und der aktuellen Zeigerlage,
    // nie aus dem vorigen Ergebnis — sonst summierte sich Rundungsdrift über
    // die Geste auf, und ein Zurückziehen führte nicht mehr dorthin, wo man
    // hergekommen ist.
    let pointer = origin
    let last: Entry[] = start
    let lines: AlignmentLines = { vertical: [], horizontal: [], measures: [] }

    const apply = () => {
      const result = compute(start, pointer, origin)
      last = start.map((entry, index) => ({ id: entry.id, rect: result.rects[index]! }))
      lines = { vertical: result.vertical, horizontal: result.horizontal, measures: result.measures }
      return last
    }

    const handleMove = (moveEvent: PointerEvent) => {
      if (onPastThreshold && !pastThreshold) {
        const dx = moveEvent.clientX - originClient.x
        const dy = moveEvent.clientY - originClient.y
        if (Math.hypot(dx, dy) > DUPLICATE_DRAG_THRESHOLD) {
          pastThreshold = true
          onPastThreshold(start)
        }
      }
      pointer = optionsRef.current.toFlow({ x: moveEvent.clientX, y: moveEvent.clientY })
      optionsRef.current.onChange(apply(), lines)
    }

    const stop = () => {
      target.releasePointerCapture?.(event.pointerId)
      target.removeEventListener('pointermove', handleMove)
      target.removeEventListener('pointerup', handleUp)
      target.removeEventListener('pointercancel', handleCancel)
      window.removeEventListener('keydown', handleKeyDown, true)
    }

    const handleUp = () => {
      stop()
      optionsRef.current.onCommit(apply(), lines)
    }

    /**
     * Abbrechen: die Ausgangslage festschreiben, als wäre nie gezogen worden.
     *
     * Über `onCommit` und nicht über `onChange`: Der Aufrufer räumt dort seine
     * Linien weg und gibt die Geste frei. Geschrieben wird die unveränderte
     * Lage — ein Schreibvorgang mehr, dafür ein Weg weniger.
     */
    const handleCancel = () => {
      stop()
      optionsRef.current.onCommit(start, { vertical: [], horizontal: [], measures: [] })
    }

    /**
     * Escape gehört der laufenden Geste, nicht der Auswahl.
     *
     * Ohne das riefe `useCanvasEscape` sein `clearSelection`: Der
     * `SelectionOverlay` verschwände mitsamt dem Element, das den Zeiger
     * hält — `pointerup` käme nie an, und die Notizen blieben auf halbem Weg
     * stehen, ohne je geschrieben zu werden.
     *
     * In der ERFASSUNGSPHASE, um vor dem Listener am Canvas-Element
     * dranzukommen.
     */
    const handleKeyDown = (keyEvent: KeyboardEvent) => {
      if (keyEvent.key !== 'Escape') return
      keyEvent.preventDefault()
      keyEvent.stopPropagation()
      handleCancel()
    }

    target.addEventListener('pointermove', handleMove)
    target.addEventListener('pointerup', handleUp)
    target.addEventListener('pointercancel', handleCancel)
    window.addEventListener('keydown', handleKeyDown, true)
  }, [])

  /**
   * Skalieren — mit Raster und Ausrichtung, schon während des Ziehens, wie in
   * FigJam.
   *
   * Das Rastern war zwischenzeitlich abgeschaltet, weil die Auswahl dabei
   * zerfiel: Solange Breite, Höhe und Position EINZELN gerastert wurden,
   * erreichten sie ihre Schwellen bei verschiedenen Faktoren, und es bewegte
   * sich mal nur die eine, mal nur die andere Notiz.
   *
   * Seit `getGroupResize` den FAKTOR rastert statt der Maße, ist das erledigt:
   * Der Faktor ändert sich nur in Stufen, und an jeder Stufe springt alles
   * zugleich — Größen wie Positionen, jede Notiz. Für die Ausrichtung gilt
   * dasselbe: Sie bestimmt einen Faktor, keine Einzelmaße.
   */
  const startResize = useCallback((corner: ResizeCorner, event: React.PointerEvent) => {
    begin(event, (start, pointer) => getGroupResizeAlignment({
      rects: start.map(entry => entry.rect),
      corner,
      pointer,
      limits: optionsRef.current.limits,
      grid: optionsRef.current.grid,
      others: optionsRef.current.getAlignTargets(),
      threshold: optionsRef.current.threshold(),
    }))
  }, [begin])

  /**
   * Verschieben. Hier darf live ausgerichtet und gerastert werden: Alle
   * bekommen DENSELBEN Versatz, springen also ohnehin zeitgleich — und das
   * sichtbare Einrasten ist dort erwünscht, genau wie beim Ziehen einer
   * einzelnen Notiz.
   */
  const startMove = useCallback((event: React.PointerEvent) => {
    // Alt EINMAL beim Anfassen lesen. Wer sie unterwegs drückt, dupliziert
    // nicht; wer sie unterwegs loslässt, dupliziert trotzdem. Fortlaufend zu
    // entscheiden hieße, die Kopien mitten in der Geste anzulegen und wieder
    // zu löschen, mitsamt den Sync-Einträgen dafür.
    const alt = event.altKey
    begin(event, (start, pointer, origin) => getGroupAlignment(
      start.map(entry => entry.rect),
      { x: pointer.x - origin.x, y: pointer.y - origin.y },
      optionsRef.current.getAlignTargets(),
      optionsRef.current.threshold(),
      optionsRef.current.grid,
    ), alt ? start => optionsRef.current.onDuplicate?.(start) : undefined)
  }, [begin])

  return { startResize, startMove }
}
